import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import type {
  AgentVersionSummary,
  AgentVersionDetail,
  AgentDetail,
} from '../../types/agent-studio';
import {
  AgentStatusPill,
  PromptDiffViewer,
} from '../../components/agent-studio';

export default function AgentStudioVersions() {
  const { system_name, version_id } = useParams<{
    system_name: string;
    version_id?: string;
  }>();

  if (!system_name) {
    return <ErrorCard message="system_name не указан." />;
  }

  if (version_id) {
    return <VersionDetailView systemName={system_name} versionId={version_id} />;
  }

  return <VersionListView systemName={system_name} />;
}

function VersionListView({ systemName }: { systemName: string }) {
  const navigate = useNavigate();
  const [versions, setVersions] = useState<AgentVersionSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchVersions() {
      try {
        setLoading(true);
        setError(null);
        const response = await api.listAgentVersions(systemName);
        if (!cancelled) {
          // Reverse-defensively to ensure newest first
          const sorted = [...response.versions].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          setVersions(sorted);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchVersions();
    return () => {
      cancelled = true;
    };
  }, [systemName]);

  return (
    <div style={{ padding: '24px' }}>
      <div className="t-eyebrow" style={{ color: 'var(--ink-secondary)', marginBottom: '16px' }}>
        <Link to="/agents" style={{ color: 'inherit', textDecoration: 'none' }}>Студия агентов</Link>
        {' / '}
        <Link to={`/agents/${systemName}`} style={{ color: 'inherit', textDecoration: 'none' }}>{systemName}</Link>
        {' / Версии'}
      </div>

      <h1 className="t-display" style={{ marginBottom: '8px' }}>
        Версии агента: {systemName}
      </h1>
      <p className="t-body" style={{ color: 'var(--ink-secondary)', marginBottom: '24px' }}>
        История версий конфигурации. Текущая активная версия выделена.
      </p>

      {loading && <LoadingRows count={3} />}
      {!loading && error && <ErrorCard message={error} />}
      {!loading && !error && versions && versions.length === 0 && (
        <EmptyCard message="Версии не найдены." />
      )}
      {!loading && !error && versions && versions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {versions.map(version => (
            <VersionRow
              key={version.id}
              version={version}
              systemName={systemName}
              onClick={() => navigate(`/agents/${systemName}/versions/${version.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function VersionDetailView({ systemName, versionId }: { systemName: string; versionId: string }) {
  const navigate = useNavigate();
  const [version, setVersion] = useState<AgentVersionDetail | null>(null);
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  async function handleActivate() {
    if (!version) return;
    if (!window.confirm(
      `Активировать версию v${version.version_number}? ` +
      `Текущая активная версия будет переведена в архив.`
    )) {
      return;
    }
    setActivating(true);
    setActivateError(null);
    try {
      await api.activateAgentVersion(systemName, versionId);
      const [updatedAgent, updatedVersion] = await Promise.all([
        api.getAgent(systemName),
        api.getAgentVersion(systemName, versionId),
      ]);
      setAgent(updatedAgent);
      setVersion(updatedVersion);
    } catch (err) {
      setActivateError(err instanceof Error ? err.message : String(err));
    } finally {
      setActivating(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const [versionRes, agentRes] = await Promise.all([
          api.getAgentVersion(systemName, versionId),
          api.getAgent(systemName),
        ]);
        if (!cancelled) {
          setVersion(versionRes);
          setAgent(agentRes);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [systemName, versionId]);

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <LoadingRows count={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <ErrorCard message={error} />
      </div>
    );
  }

  if (!version || !agent) {
    return (
      <div style={{ padding: '24px' }}>
        <ErrorCard message="Версия не найдена" />
      </div>
    );
  }

  const createdDate = new Date(version.created_at).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const isActive = version.id === agent.active_version_id;

  return (
    <div style={{ padding: '24px' }}>
      <div className="t-eyebrow" style={{ color: 'var(--ink-secondary)', marginBottom: '16px' }}>
        <Link to="/agents" style={{ color: 'inherit', textDecoration: 'none' }}>Студия агентов</Link>
        {' / '}
        <Link to={`/agents/${systemName}`} style={{ color: 'inherit', textDecoration: 'none' }}>{systemName}</Link>
        {' / '}
        <Link to={`/agents/${systemName}/versions`} style={{ color: 'inherit', textDecoration: 'none' }}>Версии</Link>
        {` / v${version.version_number}`}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
        <h1 className="t-display" style={{ margin: 0 }}>
          Версия v{version.version_number}
        </h1>
        <AgentStatusPill status={version.status} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div className="t-body-sm" style={{ color: 'var(--ink-secondary)' }}>
          Создана {createdDate} {version.created_by}
        </div>
        <button
          onClick={() => navigate(`/agents/${systemName}/versions`)}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--navy)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          ← К списку версий
        </button>
      </div>

      {version.status === 'draft' && (
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px',
          padding: '16px 20px',
          background: 'var(--bg-subtle, rgba(0,0,0,0.03))',
          borderRadius: '4px',
          border: '1px dashed var(--border-subtle, rgba(0,0,0,0.12))',
        }}>
          <div style={{ flex: 1 }}>
            <div className="t-eyebrow" style={{ color: 'var(--ink-secondary)', marginBottom: '4px' }}>
              ЭТО ЧЕРНОВИК
            </div>
            <div className="t-body-sm" style={{ color: 'var(--ink-secondary)' }}>
              Эта версия ещё не активирована. Активируйте, чтобы агент
              начал использовать её в работе, или продолжите редактирование.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => navigate(`/agents/${systemName}/edit?source_version_id=${version.id}`)}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                color: 'var(--navy)',
                border: '1px solid var(--navy)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              Редактировать эту версию
            </button>
            <button
              onClick={handleActivate}
              disabled={activating}
              style={{
                padding: '8px 16px',
                background: activating ? 'var(--ink-secondary)' : 'var(--navy)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: activating ? 'wait' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              {activating ? 'Активация...' : 'Активировать эту версию'}
            </button>
          </div>
        </div>
      )}

      {activateError && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          background: 'rgba(198, 40, 40, 0.08)',
          borderLeft: '3px solid var(--red, #C0392B)',
          color: 'var(--red, #C0392B)',
          fontSize: '13px',
        }}>
          Не удалось активировать: {activateError}
        </div>
      )}

      <h2 className="t-eyebrow" style={{ marginBottom: '16px' }}>
        Конфигурация
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '32px' }}>
        <ConfigSection
          title="СИСТЕМНЫЕ ИНСТРУКЦИИ"
          content={version.system_instructions || ''}
          contentType="text"
        />
        <ConfigSection
          title="ШАБЛОН ПРОМПТА"
          content={version.prompt_template || ''}
          contentType="text"
        />
        <ConfigSection
          title="ДОПОЛНИТЕЛЬНЫЕ ПАРАМЕТРЫ"
          content={JSON.stringify(version.extra_config || {}, null, 2)}
          contentType="json"
        />
      </div>

      <h2 className="t-eyebrow" style={{ marginBottom: '16px' }}>
        Сравнение с активной версией
      </h2>
      <div className="card" style={{ padding: '24px' }}>
        <div className="t-body-sm" style={{ color: 'var(--ink-secondary)', marginBottom: '24px' }}>
          Изменения относительно текущей активной версии (v{agent.active_version?.version_number || '?'})
        </div>

        {isActive ? (
          <div className="t-body">
            Это активная версия — сравнивать не с чем.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '8px' }}>СИСТЕМНЫЕ ИНСТРУКЦИИ</div>
              <PromptDiffViewer
                oldText={agent.active_version?.system_instructions || ''}
                newText={version.system_instructions || ''}
              />
            </div>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '8px' }}>ШАБЛОН ПРОМПТА</div>
              <PromptDiffViewer
                oldText={agent.active_version?.prompt_template || ''}
                newText={version.prompt_template || ''}
              />
            </div>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '8px' }}>ДОПОЛНИТЕЛЬНЫЕ ПАРАМЕТРЫ</div>
              <PromptDiffViewer
                oldText={JSON.stringify(agent.active_version?.extra_config || {}, null, 2)}
                newText={JSON.stringify(version.extra_config || {}, null, 2)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VersionRow({ version, systemName: _systemName, onClick }: {
  version: AgentVersionSummary;
  systemName: string;
  onClick: () => void;
}) {
  const createdDate = new Date(version.created_at).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div
      className="card"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px 24px',
        cursor: 'pointer',
        borderLeft: version.status === 'active' ? '3px solid var(--navy)' : undefined,
        transition: 'border-color 0.15s ease',
      }}
      onMouseEnter={e => {
        if (version.status !== 'active') {
          e.currentTarget.style.borderLeft = '3px solid var(--ink-secondary)';
        }
      }}
      onMouseLeave={e => {
        if (version.status !== 'active') {
          e.currentTarget.style.borderLeft = '';
        }
      }}
    >
      <div style={{ width: '120px' }}>
        <AgentStatusPill status={version.status} />
      </div>
      <div className="t-body" style={{ fontWeight: 600, width: '80px' }}>
        v{version.version_number}
      </div>
      <div className="t-body" style={{ color: 'var(--ink-secondary)', flex: 1 }}>
        {createdDate}
      </div>
      <div className="t-body-sm" style={{ color: 'var(--ink-secondary)', marginRight: '24px' }}>
        {version.created_by}
      </div>
      <div style={{ color: 'var(--ink-secondary)', fontSize: '20px' }}>
        ›
      </div>
    </div>
  );
}

function ConfigSection({ title, content, contentType: _contentType }: {
  title: string;
  content: string;
  contentType: 'text' | 'json';
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="card" style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div className="t-eyebrow">{title}</div>
        <button
          onClick={handleCopy}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--navy)',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          {copied ? 'Скопировано' : 'Скопировать'}
        </button>
      </div>
      <pre style={{
        margin: 0,
        padding: '16px',
        background: 'var(--bg-subtle)',
        borderRadius: '4px',
        fontSize: '13px',
        fontFamily: 'var(--font-mono)',
        color: 'var(--ink-primary)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}>
        {content || <span style={{ color: 'var(--ink-secondary)' }}>—</span>}
      </pre>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div
      className="card"
      style={{
        marginTop: '24px',
        padding: '32px',
        borderLeft: '3px solid var(--red, #C0392B)',
      }}
    >
      <div className="t-eyebrow" style={{ color: 'var(--red, #C0392B)' }}>
        Ошибка загрузки
      </div>
      <p className="t-body" style={{ marginTop: '8px' }}>
        {message}
      </p>
    </div>
  );
}

function EmptyCard({ message }: { message: string }) {
  return (
    <div
      className="card"
      style={{
        marginTop: '24px',
        padding: '32px',
        textAlign: 'center',
      }}
    >
      <p className="t-body" style={{ color: 'var(--ink-secondary)' }}>
        {message}
      </p>
    </div>
  );
}

function LoadingRows({ count }: { count: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div
      className="card"
      style={{
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        opacity: 0.4,
      }}
    >
      <div style={{
        background: 'var(--bg-subtle)',
        height: '24px',
        width: '80px',
        borderRadius: '12px',
      }} />
      <div style={{
        background: 'var(--bg-subtle)',
        height: '20px',
        width: '40px',
        borderRadius: '4px',
      }} />
      <div style={{
        background: 'var(--bg-subtle)',
        height: '20px',
        flex: 1,
        borderRadius: '4px',
      }} />
      <div style={{
        background: 'var(--bg-subtle)',
        height: '20px',
        width: '120px',
        borderRadius: '4px',
      }} />
    </div>
  );
}
